export const BACKGROUND_STYLES = {
    preset: "linear-gradient(135deg, #0B66E4 0%, #5B6EF5 48%, #7C3AED 100%)"
};

export const BACKGROUND_IMAGE_URLS = {
    office: "",
    preset: "",
    workspace: ""
};

export const CUSTOM_BACKGROUND_STORAGE_KEY = "meBusinessCallCustomBackgrounds";
export const CUSTOM_BACKGROUND_SELECTED_KEY = "meBusinessCallSelectedCustomBackground";
export const LEGACY_CUSTOM_BACKGROUND_STORAGE_KEY = "meBusinessCallCustomBackground";

export const BACKGROUND_EFFECTS = [
    { value: "none", label: "None", description: "Original camera", supported: true },
    { value: "blur", label: "Blur", description: "Local preview blur", supported: true },
    { value: "strong-blur", label: "Strong Blur", description: "Stronger local blur", supported: true },
    { value: "preset", label: "Preset background", description: "Me Business preset background", supported: true, thumbnail: BACKGROUND_STYLES.preset }
];

export const normalizeBackgroundEffect = (effect = "none") => {
    if (["office", "workspace", "gradient"].includes(effect)) {
        return "preset";
    }

    return BACKGROUND_EFFECTS.some(option => option.value === effect) || effect === "custom"
        ? effect
        : "none";
};

export const getStoredCustomBackgrounds = () => {
    const storedBackgrounds = localStorage.getItem(CUSTOM_BACKGROUND_STORAGE_KEY);
    let backgrounds = [];

    if (storedBackgrounds) {
        try {
            const parsedBackgrounds = JSON.parse(storedBackgrounds);
            backgrounds = Array.isArray(parsedBackgrounds)
                ? parsedBackgrounds.filter(background => background?.id && background?.url)
                : [];
        } catch (error) {
            console.warn("Unable to read saved call backgrounds.", error);
        }
    }

    const legacyBackgroundUrl = localStorage.getItem(LEGACY_CUSTOM_BACKGROUND_STORAGE_KEY);
    if (legacyBackgroundUrl && !backgrounds.some(background => background.url === legacyBackgroundUrl)) {
        backgrounds = [
            ...backgrounds,
            {
                id: "legacy-custom-background",
                label: "Custom 1",
                url: legacyBackgroundUrl
            }
        ];
    }

    return backgrounds;
};

export const saveStoredCustomBackgrounds = (backgrounds) => {
    localStorage.setItem(CUSTOM_BACKGROUND_STORAGE_KEY, JSON.stringify(backgrounds));
};

export const getSelectedCustomBackgroundUrl = () => {
    const backgrounds = getStoredCustomBackgrounds();
    const selectedId = localStorage.getItem(CUSTOM_BACKGROUND_SELECTED_KEY);
    const selectedBackground = backgrounds.find(background => background.id === selectedId) || backgrounds[0];

    return selectedBackground?.url || "";
};

const normalizeCallParticipantName = displayName => (displayName || "")
    .replace(/\s+\((?:Guest|Unknown Device)\)$/i, "")
    .replace(/\s+\(@meet_[^\s()]+:[^\s()]+\)$/i, "")
    .trim();

export const getInitials = name => {
    const initials = normalizeCallParticipantName(name)
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0])
        .join("")
        .toUpperCase();

    return initials || "?";
};

export const getCallParticipantLabel = (matrixUserId, displayName) => {
    const normalizedName = normalizeCallParticipantName(displayName);

    if (matrixUserId?.startsWith?.("@meet_") && normalizedName) {
        return `${normalizedName} (Guest)`;
    }

    return normalizedName;
};

export const deviceLabel = (device, fallback) => device.label || fallback;

export const attachVideoStream = (videoElement, stream) => {
    if (!videoElement) {
        return;
    }

    if (videoElement.srcObject !== stream) {
        videoElement.srcObject = stream || null;
    }

    if (stream) {
        videoElement.play().catch(error => {
            console.warn("Unable to autoplay call video.", error);
        });
    }
};

export const getBrowserCallCapabilities = () => {
    const mediaDevices = navigator.mediaDevices || {};
    const canUseMedia = Boolean(mediaDevices.getUserMedia);

    return [
        {
            label: "Camera and microphone",
            supported: canUseMedia,
            detail: canUseMedia ? "Available" : "Not supported by this browser"
        },
        {
            label: "Speaker selection",
            supported: typeof HTMLMediaElement !== "undefined" && Boolean(HTMLMediaElement.prototype.setSinkId),
            detail: "Required for choosing audio output devices"
        },
        {
            label: "Screen sharing",
            supported: Boolean(mediaDevices.getDisplayMedia),
            detail: "Required for Share Screen"
        },
        {
            label: "Virtual background replacement",
            supported: Boolean(navigator.mediaDevices?.getUserMedia) && typeof HTMLCanvasElement !== "undefined",
            detail: "Requires MediaPipe model assets in public/models"
        }
    ];
};
